import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import './login.css';
import { all_routes } from 'core/data/routes/all_routes';
import LoginForm from '../common/auth/LoginForm';
import VerificationCodeModal from '../common/auth/VerificationCodeModal';
import ForgotPasswordModal from '../common/auth/ForgotPasswordModal';
import ResetPasswordModal from '../common/auth/ResetPasswordModal';
import { postData } from 'core/services/api';

const INITIAL_VERIFICATION_TIME = 5 * 60;

const Login = () => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [loginVerified, setLoginVerified] = useState(false);
    const [usuario, setUsuario] = useState('');
    const [email, setEmail] = useState('');
    const [codigo, setCodigo] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState('');
    const [timeLeft, setTimeLeft] = useState(INITIAL_VERIFICATION_TIME);
    const history = useHistory();

    useEffect(() => {
        if (!showVerificationModal || timeLeft <= 0) return undefined;

        const timer = window.setInterval(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        return () => window.clearInterval(timer);
    }, [showVerificationModal, timeLeft]);

    const formatTimeLeft = () => {
        const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
        const seconds = (timeLeft % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const resetVerificationState = (preserveCode = false) => {
        setShowVerificationModal(false);
        setVerificationError('');
        setVerificationLoading(false);
        setTimeLeft(INITIAL_VERIFICATION_TIME);
        if (!preserveCode) {
            setCodigo('');
        }
    };

    const resetForgotPasswordState = () => {
        setShowForgotPasswordModal(false);
        setEmail('');
        setForgotPasswordError('');
        setForgotPasswordLoading(false);
    };

    const resetPasswordState = () => {
        setShowResetPasswordModal(false);
        setPassword('');
        setConfirmPassword('');
        setResetPasswordError('');
        setResetPasswordLoading(false);
        setCodigo('');
    };

    const handleSubmit = async ({ usuario: submittedUsuario, password }) => {
        setError('');
        setLoading(true);

        try {
            const response = await postData('/login', {
                usuario: submittedUsuario,
                password,
            });

            if (response?.status === 200) {
                setUsuario(submittedUsuario);
                setLoginVerified(true);
                setShowVerificationModal(true);
                setCodigo('');
                setVerificationError('');
                setTimeLeft(INITIAL_VERIFICATION_TIME);
                return;
            }

            setError('No se pudo iniciar sesión. Intente nuevamente.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'Nombre de usuario o contraseña incorrectos';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setVerificationError('');
        setVerificationLoading(true);

        try {
            const response = await postData('/login/verify-code', {
                usuario,
                codigo,
            });

            if (response?.status === 200) {
                resetVerificationState(true);

                if (loginVerified) {
                    localStorage.setItem('isAdmin', 'true');
                    localStorage.setItem('usuario', usuario);
                    setPassword('');
                    setConfirmPassword('');
                    setLoginVerified(false);
                    history.push(all_routes.homeBannerSlider);
                    return;
                }

                setShowResetPasswordModal(true);
                return;
            }

            setVerificationError('El código ingresado es incorrecto o ha expirado.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'No se pudo validar el código.';
            setVerificationError(message);
        } finally {
            setVerificationLoading(false);
        }
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setForgotPasswordError('');
        setForgotPasswordLoading(true);

        try {
            const response = await postData('/login/send-code', {
                email,
                usuario,
            });

            if (response?.status === 200) {
                setLoginVerified(false);
                setShowForgotPasswordModal(false);
                setShowVerificationModal(true);
                setCodigo('');
                setVerificationError('');
                setTimeLeft(INITIAL_VERIFICATION_TIME);
                return;
            }

            setForgotPasswordError('No se pudo enviar el código. Intente nuevamente.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'No se pudo enviar el código.';
            setForgotPasswordError(message);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const handleResetPasswordSubmit = async (e) => {
        e.preventDefault();
        setResetPasswordError('');

        if (password !== confirmPassword) {
            setResetPasswordError('Las contraseñas no coinciden.');
            return;
        }

        setResetPasswordLoading(true);

        try {
            const response = await postData('/login/update', {
                usuario,
                password,
                email,
                codigo,
            });

            if (response?.status === 200) {
                localStorage.setItem('isAdmin', 'true');
                localStorage.setItem('usuario', usuario);
                resetPasswordState();
                setLoginVerified(false);
                history.push(all_routes.homeBannerSlider);
                return;
            }

            setResetPasswordError('No se pudo actualizar la contraseña. Intente nuevamente.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.response?.data?.error || 'No se pudo actualizar la contraseña.';
            setResetPasswordError(message);
        } finally {
            setResetPasswordLoading(false);
        }
    };

    return (
        <>
            <LoginForm
                onSubmit={handleSubmit}
                error={error}
                loading={loading}
                formClassName="mb-2"
            />

            <div className="text-center mt-2">
                <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => {
                        setForgotPasswordError('');
                        setShowForgotPasswordModal(true);
                    }}
                >
                    ¿Olvidaste tu contraseña?
                </button>
            </div>

            <ForgotPasswordModal
                show={showForgotPasswordModal}
                onHide={resetForgotPasswordState}
                onSubmit={handleForgotPasswordSubmit}
                loading={forgotPasswordLoading}
                error={forgotPasswordError}
                email={email}
                onEmailChange={setEmail}
                usuario={usuario}
                onUsuarioChange={setUsuario}
            />

            <VerificationCodeModal
                show={showVerificationModal}
                onHide={resetVerificationState}
                onSubmit={handleVerifyCode}
                loading={verificationLoading}
                error={verificationError}
                code={codigo}
                onCodeChange={setCodigo}
                timeLeft={timeLeft}
            />

            <ResetPasswordModal
                show={showResetPasswordModal}
                onHide={resetPasswordState}
                onSubmit={handleResetPasswordSubmit}
                loading={resetPasswordLoading}
                error={resetPasswordError}
                password={password}
                onPasswordChange={setPassword}
                confirmPassword={confirmPassword}
                onConfirmPasswordChange={setConfirmPassword}
            />
        </>
    );
};

export default Login;